<template>
  <div>
    <v-card>
      <v-card-title>
        Marka Rapor Portalları
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Marka ara"
          single-line
          hide-details
        ></v-text-field>
      </v-card-title>

      <v-card-subtitle class="pt-2">
        Markaya verilen link, o markanın tüm içerik kampanyalarının özet raporunu
        gösterir. Katılımcı kimlikleri maskelenir; e-posta, yüklenen dosya ve
        revizyon notu paylaşılmaz.
      </v-card-subtitle>

      <v-data-table
        :headers="headers"
        :items="filteredItems"
        :loading="loading"
        :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      >
        <template v-slot:item.password="{ item }">
          <span v-if="!item.accessToken" class="grey--text">—</span>
          <template v-else>
            <span class="mono">{{
              revealed[item.companyId] ? item.password : "••••••••"
            }}</span>
            <v-btn icon x-small class="ml-1" @click="toggleReveal(item)">
              <v-icon x-small>{{
                revealed[item.companyId] ? "mdi-eye-off" : "mdi-eye"
              }}</v-icon>
            </v-btn>
            <v-btn icon x-small @click="copy(item.password, 'Şifre kopyalandı')">
              <v-icon x-small>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            x-small
            :color="item.accessToken ? 'success' : 'grey lighten-1'"
            text-color="white"
          >
            {{ item.accessToken ? "Açık" : "Kapalı" }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn
            v-if="!item.accessToken"
            small
            color="primary"
            depressed
            @click="ensureAccess(item)"
          >
            <v-icon left small>mdi-link-plus</v-icon> Erişim Aç
          </v-btn>

          <template v-else>
            <v-btn small text color="primary" @click="copyLink(item)">
              <v-icon left small>mdi-link</v-icon> Linki kopyala
            </v-btn>
            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on">
                  <v-icon small>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="regenerate(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small>mdi-refresh</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Yeni şifre üret</v-list-item-title>
                </v-list-item>
                <v-list-item @click="revoke(item)">
                  <v-list-item-icon class="mr-2">
                    <v-icon small color="error">mdi-link-off</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title class="error--text">Erişimi kapat</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script lang="ts">
import NucleusComponentBase from "@/shared/application/nucleus-component-base";
import { Component } from "vue-property-decorator";

@Component
export default class BrandPortalAccess extends NucleusComponentBase {
  loading = true;
  items: any[] = [];
  search = "";
  revealed: { [id: string]: boolean } = {};

  headers = [
    { text: "Marka", value: "companyName" },
    { text: "Kampanya", value: "campaignCount" },
    { text: "Katılımcı", value: "influencerCount" },
    { text: "Durum", value: "status", sortable: false },
    { text: "Şifre", value: "password", sortable: false },
    { text: "", value: "actions", sortable: false, align: "end" },
  ];

  get filteredItems(): any[] {
    const q = (this.search || "").trim().toLowerCase();
    return this.items.filter(
      (x) => !q || (x.companyName || "").toLowerCase().includes(q)
    );
  }

  mounted() {
    this.load();
  }

  async load() {
    this.loading = true;
    const res = await this.nucleusService.get<any>("/api/BrandPortal/access", false);
    if (!res.isError) this.items = res.content || [];
    this.loading = false;
  }

  toggleReveal(item: any) {
    this.$set(this.revealed, item.companyId, !this.revealed[item.companyId]);
  }

  brandLink(item: any): string {
    const base = window.location.origin.replace(/\/$/, "");
    return `${base}/brand/${item.accessToken}`;
  }

  async copyLink(item: any) {
    await this.copy(this.brandLink(item), "Marka rapor linki kopyalandı");
  }

  async ensureAccess(item: any) {
    const res = await this.nucleusService.post<any>(
      `/api/BrandPortal/companies/${item.companyId}/access`,
      {}
    );
    if (!res.isError) {
      item.accessToken = res.content.accessToken;
      item.password = res.content.password;
      this.$set(this.revealed, item.companyId, true);
      this.swalToast(2000, "success", "Erişim açıldı, şifre üretildi");
    } else {
      this.swalToast(2500, "error", "İşlem başarısız");
    }
  }

  async regenerate(item: any) {
    const r = await this.swalConfirm(
      "Yeni şifre üretilsin mi? Markanın elindeki eski şifre geçersiz olur."
    );
    if (!(r as any).value) return;

    const res = await this.nucleusService.post<any>(
      `/api/BrandPortal/companies/${item.companyId}/regenerate-password`,
      {}
    );
    if (!res.isError) {
      item.password = res.content.password;
      this.$set(this.revealed, item.companyId, true);
      this.swalToast(1500, "success", "Yeni şifre üretildi");
    } else {
      this.swalToast(2500, "error", "İşlem başarısız");
    }
  }

  async revoke(item: any) {
    const r = await this.swalConfirm(
      "Erişim kapatılsın mı? Link ve şifre silinir, marka rapora ulaşamaz."
    );
    if (!(r as any).value) return;

    const res = await this.nucleusService.post<any>(
      `/api/BrandPortal/companies/${item.companyId}/revoke`,
      {}
    );
    if (!res.isError) {
      item.accessToken = null;
      item.password = null;
      this.swalToast(1500, "success", "Erişim kapatıldı");
    } else {
      this.swalToast(2500, "error", "İşlem başarısız");
    }
  }

  async copy(text: string, msg = "Kopyalandı") {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    this.swalToast(1500, "success", msg);
  }
}
</script>

<style scoped>
.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  letter-spacing: 0.04em;
}
</style>
